import { NotificationService } from './notification-service';
import { Observable, forkJoin, of, merge, from } from 'rxjs';
import { NotificationItem } from '../models/notification-item';
import { ActivityFeedNotificationItem, ActivityFeedNotificationContext } from '../models/activity-feed-notification-item';
import { pdsUtils } from '../pds-utils';
import { SectionStreamActivityRI, SectionRI, SectionActivityNotificationRI, StudentRI } from 'common-utils/rest-interfaces';
import { SectionStreamActivityUpdate } from 'common-utils/interfaces';
import { ActivityFeedNotificationServiceContext } from '../interfaces/activity-feed-notification-service-context';
import { pdsHttpUtils } from '../pds-http-utils';
import { AjaxResponse } from '../interfaces/AjaxResponse';
import { ActivityFeedNotificationServiceConfig } from '../interfaces/ActivityFeedNotificationServiceConfig';
import { filter } from 'rxjs/operators';
import { map, mergeMap } from 'rxjs/operators';
import { publishLast } from 'rxjs/operators';
import { refCount } from 'rxjs/operators';
import { GetStreamCacheService } from './get-stream-cache-service';

// these types are defined like this because they shouldn't be used anywhere but this file.
type ActivityUpdateWithFeedName = SectionStreamActivityUpdate & { feedName: string };
type GetStreamNotificationFeedResponse = {feedName: string, results: Array<SectionActivityNotificationRI>};

export class ActivityFeedNotificationService extends NotificationService {
  // when we expand on this type is going to need to be ACTIVITY_FEED_NOTIFICATION-{someUniqueId} and that type will
  // need to be passed into ActivityFeedNotificationItem so that the aggregated-notification-service can map back to the correct service
  public type: string = ActivityFeedNotificationItem.NOTIFICATION_TYPE;

  private config: ActivityFeedNotificationServiceConfig;
  private clientFeeds: Array<any> = [];
  private mergedClientFeeds: Observable<NotificationItem>;
  private clientFeedNameMap: { [name: string]: any } = {};  // GetStream feedname to feed object mapping
  private context: ActivityFeedNotificationServiceContext;
  private clientFeedsInitialized: Observable<any>;
  private sectionToStudentsMap = new Map<string, Array<StudentRI>>();
  private seenActivitiesMap: {[id: string]: boolean} = {};

  constructor(setup: ActivityFeedNotificationServiceConfig, private client: any) {
    super(setup);
    this.config = setup;
    this.context = setup.context;

    if (this.context == null) { // validation
      throw 'Activity Feed Notification Service requires context';
    }

    if (this.context.sectionMap == null) {  // initialize section map if needed
      this.context.sectionMap = Object.assign({}, ...this.context.sections.map((section) => {
        return { [section.uid]: section };
      }));
    }

    if (this.context.students != null && this.context.studentMap == null) { // initialize student map if needed
      this.context.studentMap = Object.assign({}, ...this.context.students.map((student) => {
        return { [student.uid]: student };
      }));

      if (this.context.studentClassScheduleMap != null) {
        for (let student of this.context.students) {
          let classSchedule = this.context.studentClassScheduleMap.get(student.uid);
          if (classSchedule != null) {
            for (let section of classSchedule.sections) {
              if (this.sectionToStudentsMap.has(section.uid)) {
                this.sectionToStudentsMap.get(section.uid)!.push(student);
              } else {
                this.sectionToStudentsMap.set(section.uid, [student]);
              }
            }
          }
        }
      }
    } else if (this.context.studentMap == null) {
      this.context.studentMap = {};
    }

    this.clientFeedsInitialized = forkJoin(  // get token for each feed and use it to initialize the feed.
      this.config.getStream.feedGroups.map((groupName: string) => {
        let payload = {
          feedUid: this.config.getStream.feedIdentifier,
          feedGroup: groupName
        };
        let path = (this.config.path == null) ? '/communication/v1/token' : this.config.path;
        let url = this.config.domain != null ? this.config.domain + path : path;
        return pdsHttpUtils.post(url, payload, {}, pdsHttpUtils.addUCAuthHeader({})).pipe(
          map((response: AjaxResponse) => {
            let data: { feedGroup: string, token: string, uid: string } = JSON.parse(response.data);
            let feedName = `${this.context.userType}-${this.config.getStream.feedIdentifier}`;
            let gsFeed = this.client.feed(groupName, feedName, data.token);
            this.clientFeedNameMap[feedName] = gsFeed;
            this.clientFeeds.push(gsFeed);
          })
        );
      })
    ).pipe(publishLast(), refCount());
  }

  initializeNotificationStream(): Observable<any> {
    let filterCallback = (update: ActivityUpdateWithFeedName): boolean => {
      if (update.newActivity == null || update.newActivity.id == null || update.newActivity.origin == null) {
        return false;
      }
      // the '!'s in the following code should be safe becasue of the null checks above (won't be necessary once we figure out why dupes are coming through)
      // I've contacted GetStream because these duplicates seem like a problem in their code.
      let activityIdentifier = update.newActivity.origin + update.newActivity.id!;
      let isValid = update.newActivity.sender_uid !== this.context.currentUserUid &&
                    this.seenActivitiesMap[activityIdentifier] !== true;
      if (!isValid) {
        return false;
      }
      this.seenActivitiesMap[activityIdentifier] = true;
      return true;

    };

    return this.clientFeedsInitialized.pipe(
      map(() => {
        // set up real time updates
        let observables: Array<Observable<SectionStreamActivityUpdate>> = this.clientFeeds.map((feed) => {
          return this.realTimeUpdates(feed).pipe(
            map((update: SectionStreamActivityUpdate) => {
              (<ActivityUpdateWithFeedName> update).feedName = feed.id;
              return update;
            })
          );
        });

        // merge each streams updates into one
        this.mergedClientFeeds = merge(...observables).pipe(
          filter(filterCallback),
          map((update: ActivityUpdateWithFeedName) => {
            // the cast here is safe because of the filter.
            let notificationContext = this.getNotificationContext((<SectionStreamActivityRI>update.newActivity), update.feedName);
            return new ActivityFeedNotificationItem(false, false, notificationContext);
          })
        );

        this.notificationStream = this.mergedClientFeeds;
        return true;
      })
    );
  }

  getRecentNotifications(limit: number): Observable<Array<NotificationItem>> {
    return (<any>this.clientFeedsInitialized).pipe(
      mergeMap(() => {
        // get recent notification from each stream
        let obs: Array<any> = [];
        for (let feed of this.clientFeeds) {
          let observable = GetStreamCacheService.getObservable(feed.feedUrl, limit);
          if (observable == null) {
            observable = from(
              // Important:
              // - We need the feed.get promise resolved right away by calling the then() function so that the
              //   we can get the resulting data to cache along with the observable.
              feed.get({ limit: limit }).then(
                (result: GetStreamNotificationFeedResponse) => {
                  result.feedName = feed.id;
                  return result;
                })
            );
            GetStreamCacheService.cacheObservable(feed.feedUrl, limit, 'ActivityFeedNotificationService.getRecentNotifications', observable);
          }
          obs.push(observable);
        }

        // process the results of each stream
        return forkJoin(obs).pipe(
          map((streamResults: Array<GetStreamNotificationFeedResponse>) => {
            let ret: Array<NotificationItem> = [];
            for (let feedData of streamResults) {
              ret.push(...this.processRecentFeedData(feedData));
            }
            return ret;
          })
        );
      })
    );
  }

  // Returns an Observable that can emit responses multiple times
  realTimeUpdates(clientFeed): Observable<SectionStreamActivityUpdate> {
    return Observable.create((observer) => {
      // subscribe actually returns a promise
      let subscription = clientFeed.subscribe((data) => {
        // new activity added
        if (data.new && data.new.length > 0) {
          for (let newActivity of data.new) {
            observer.next({
              newActivity: newActivity
            });
          }
          GetStreamCacheService.evictObservable(clientFeed.feedUrl, 'ActivityFeedNotificationService.realTimeUpdates (new activity)');
        }
      });
    });
  }

  markAsRead(items: Array<ActivityFeedNotificationItem>): Observable<any> {
    return this.updateFeedActivities(items, 'read');
  }

  markAsSeen(items: Array<ActivityFeedNotificationItem>): Observable<any> {
    return this.updateFeedActivities(items, 'seen');
  }

  private updateFeedActivities(items: Array<ActivityFeedNotificationItem>, action: 'seen' | 'read'): Observable<void> {
    let groups = pdsUtils.groupBy(items, (item) => item.feedName);
    let observables: Array<Observable<any>> = [of(undefined)];

    for (let feedName of Object.keys(groups)) {
      let gsFeed = this.clientFeedNameMap[feedName];
      if (gsFeed == null) {
        throw 'bad feed name';
      }
      // updateFeedActivities gets called when the user clicks on the activity feed notification to navigate to activity feed.
      // This means we need to evict the related cache entries so we can get the right updates.
      GetStreamCacheService.evictObservable(gsFeed.feedUrl, 'ActivityFeedNotificationService.updateFeedActivities');

      let ids: Array<string> = groups[feedName].map((item) => item.id);
      /**
       * Urls have a length limit of 2000 characters, which we could exceed given enough notifications.
       * There are 200 chars from the api (164 + buffer), so we splice the first 45 uids from the ids
       * which is approximately 1800 characters (1755 + buffer) to ensure we don't exceed the url limit.
       */
      while (ids.length > 0) {
        let idBatch = ids.splice(0, 45);
        if (action === 'seen') {
          observables.push(from(gsFeed.get({ limit: 5, mark_seen: idBatch })));
        } else {
          observables.push(from(gsFeed.get({ limit: 5, mark_read: idBatch })));
        }
      }
    }

    return forkJoin(observables).pipe(
      map(() => {
        return;
      })
    );
  }

  private feedOriginToSection(origin?: string): SectionRI {
    let sectionUid: string = this.extractOriginUid(origin);
    return this.context.sectionMap[sectionUid];
  }

  private extractOriginUid(origin?: string): string {
    return (origin == null) ? '' : origin.slice(22); // right now origin will always be section_feeds:SECTION-{uid}
  }

  private sectionUidToStudents(sectionUid: string): Array<StudentRI> {
    switch (this.context.userType) {
      case 'STUDENT':
        return [];
      case 'PARENT':
        return this.sectionToStudentsMap.get(sectionUid) || [];
      case 'TEACHER':
        return [];
      default:
        throw 'Unsupported User type';
    }
  }

  private getNotificationContext(activity: SectionStreamActivityRI, feedName: string): ActivityFeedNotificationContext {
    let section = this.feedOriginToSection(activity.origin);
    let students = this.sectionUidToStudents(section.uid);

    return {
      section: section,
      students: students,
      feedName: feedName.split(':')[1],
      activityId: (activity.id == null) ? '' : activity.id,
      activitySenderName: activity.sender_name,
      postedAt: new Date(activity.time + 'Z'), // adding Z sets the timezone in the input string as UTC
      senderUid: activity.sender_uid,
      userType: this.context.userType
    };
  }

  private processRecentFeedData(feedData: GetStreamNotificationFeedResponse): Array<NotificationItem> {
    let ret: Array<NotificationItem> = [];
    for (let notificationRI of feedData.results) {
      if (!notificationRI.is_read) {  // if the notification is already read we don't need it.
        for (let activity of notificationRI.activities) {
          if (activity.to != null && activity.sender_uid !== this.context.currentUserUid) {
            for (let origin of activity.to) {
              if (this.context.sectionMap[this.extractOriginUid(origin)] != null) { // we only care about courses in our context
                activity.origin = origin;
                let notificationContext = this.getNotificationContext(activity, feedData.feedName);
                ret.push(new ActivityFeedNotificationItem(notificationRI.is_seen, notificationRI.is_read, notificationContext));
              }
            }
          }
        }
      }
    }
    return ret;
  }
}
